import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FaPlus, FaTimes, FaTools, FaVideo, FaLayerGroup, FaEye } from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { useTeacherTaxonomy } from "../../hooks/useTeacherTaxonomy";
import {
    ContentThumb, typeMeta, SubjectPicker, ClassPills, TypePicker, TopicSelect, SubjectIcon,
    ProfileSetupNotice,
} from "../../components/marketplace";
import { MarketplaceBadge } from "../teacher_content";

const EMPTY_FORM = { title: "", subject: "", class: "", topic: "", type: "lesson_notes" };

// Studio "Content" tab: create a new lesson, and manage which of the teacher's
// lessons are listed on the marketplace (listing keeps them free for students).
const StudioContent = ({ navigate }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);

    // Subject/class options come from the teacher's own profile (the hook also
    // surfaces the plan, so the price warning below needs no separate fetch).
    const { subjects, classes, topicsBySubject, plan, hasSubjects, hasClasses, needsSetup } = useTeacherTaxonomy();
    const savedPrice = plan?.price ?? null;

    const load = async () => {
        setLoading(true);
        try {
            const lRes = await authFetch("/api/lessons/mine");
            const lData = await lRes.json().catch(() => ({}));
            setItems(lData.data || []);
        } catch (err) {
            toast.error("Could not load your content");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (needsSetup) {
            toast.error("Set up the subjects and classes you teach first");
            return;
        }
        if (!form.title.trim() || !form.subject.trim() || !form.class.trim()) {
            toast.error("Title, subject and class are required");
            return;
        }
        setSaving(true);
        try {
            const res = await authFetch("/api/lessons", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't create content");
            toast.success("Draft created — add its segments");
            navigate(`/teacher/lessons/${data.data.id}/build`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleMarketplace = async (item) => {
        const listing = !item.in_marketplace;
        setBusyId(item.id);
        try {
            const res = await authFetch(`/api/lessons/${item.id}/marketplace`, { method: listing ? "POST" : "DELETE" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't update the marketplace");
            toast.success(data.message || (listing ? "Added to the marketplace" : "Removed from the marketplace"));
            setItems((prev) => prev.map((l) => (l.id === item.id ? { ...l, in_marketplace: listing, status: listing ? "published" : l.status } : l)));
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-bold text-gray-800">Your content</h2>
                    <p className="text-xs text-gray-500">Create lessons, then list the ones you want to sell. Listed lessons stay free for your own students.</p>
                </div>
                <button onClick={() => setShowForm((s) => !s)} className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm shrink-0">
                    <FaPlus /> <span className="hidden sm:inline">Create</span>
                </button>
            </div>

            {savedPrice == null && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3">
                    Set your monthly price in <button onClick={() => navigate("/teacher/marketplace/profile")} className="underline font-semibold">Profile</button> before listing lessons on the marketplace.
                </div>
            )}

            {showForm && (
                <form onSubmit={handleCreate} className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-5">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800">New content</h3>
                        <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>
                    {needsSetup && (
                        <ProfileSetupNotice
                            missingSubjects={!hasSubjects}
                            missingClasses={!hasClasses}
                            onSetup={() => navigate("/teacher/marketplace/profile")}
                        />
                    )}
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Title <span className="text-red-500">*</span></label>
                        <input required placeholder="e.g. Balancing chemical equations" className="border border-gray-200 rounded-xl p-2.5 w-full focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <SubjectPicker label="Subject" required allowCustom={false} subjects={subjects} value={form.subject}
                            onChange={(v) => setForm({ ...form, subject: v })} />
                        <ClassPills label="Class" required classes={classes} value={form.class}
                            onChange={(v) => setForm({ ...form, class: v })} />
                    </div>
                    <TopicSelect label="Topic (optional)" hint="Pulled from your question bank for the chosen subject — the same topics apply across every class." subject={form.subject}
                        topicsBySubject={topicsBySubject} value={form.topic} onChange={(v) => setForm({ ...form, topic: v })} />
                    <TypePicker label="Content type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
                    <button disabled={saving || needsSetup} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2">
                        <FaPlus size={13} /> {saving ? "Creating…" : "Create & add segments"}
                    </button>
                </form>
            )}

            {loading && <p className="text-gray-500 text-sm">Loading…</p>}
            {!loading && items.length === 0 && (
                <div className="text-center py-14 bg-white rounded-xl shadow-sm">
                    <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto"><FaVideo size={20} /></div>
                    <p className="mt-3 text-gray-600 font-medium">No content yet</p>
                    <p className="text-sm text-gray-400">Create your first lesson to get started.</p>
                </div>
            )}

            <div className="space-y-2">
                {items.map((l) => (
                    <div key={l.id} className="flex items-center gap-3 bg-white rounded-xl shadow-sm p-2.5">
                        <button onClick={() => navigate(`/teacher/lessons/${l.id}/build`)} className="w-24 sm:w-28 shrink-0" title="Edit lesson">
                            <ContentThumb type={l.type} thumbnailUrl={l.thumbnail_url} title={l.title} />
                        </button>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-gray-800 truncate">{l.title}</p>
                                {l.in_marketplace && <MarketplaceBadge />}
                                {l.status !== "published" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">draft</span>}
                            </div>
                            <p className="text-xs text-gray-600 mt-1 flex items-center gap-1.5 truncate">
                                <SubjectIcon subject={l.subject} size={20} iconSize={10} />
                                <span className="font-medium">{l.subject}</span>
                                {l.class && <><span className="text-gray-300">·</span><span className="text-gray-500">{l.class}</span></>}
                                {l.topic && <><span className="text-gray-300">·</span><span className="text-gray-500 truncate">{l.topic}</span></>}
                            </p>
                            <div className="flex items-center gap-2.5 text-[11px] text-gray-400 mt-1 flex-wrap">
                                <span className="text-gray-500 font-medium">{typeMeta(l.type).label}</span>
                                <span className="inline-flex items-center gap-1"><FaLayerGroup size={9} /> {l.segmentCount || 0} segment{l.segmentCount === 1 ? "" : "s"}</span>
                                <span className="inline-flex items-center gap-1"><FaEye size={9} /> {l.openedCount || 0} opens</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={() => navigate(`/teacher/lessons/${l.id}/build`)} title="Edit" className="text-gray-400 hover:text-blue-600 p-2"><FaTools size={14} /></button>
                            {l.in_marketplace ? (
                                <button onClick={() => toggleMarketplace(l)} disabled={busyId === l.id} className="text-red-500 border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50">
                                    {busyId === l.id ? "…" : "Remove"}
                                </button>
                            ) : (
                                <button onClick={() => toggleMarketplace(l)} disabled={busyId === l.id || savedPrice == null}
                                    title={savedPrice == null ? "Set your price first" : "List on the marketplace"}
                                    className="bg-blue-600 text-white hover:bg-blue-700 px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50">
                                    {busyId === l.id ? "…" : "List"}
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StudioContent;
